import { Flex, Input, InputProps, Text } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { get } from "lodash";
import { ChangeEvent, FocusEvent, useEffect, useState } from "react";

/**
 * What the server says about a write-only secret (an API key, a database
 * password). Never the secret itself: at most its last four characters and
 * when it was last verified.
 */
export interface SecretFieldStatus {
  /** A value is saved on the server. */
  set: boolean;
  /** Its last four characters, as the server reports them. */
  last4?: string | null;
  /** When the server last verified it (ISO-8601). */
  verifiedAt?: string | null;
}

/** "Saved, ending ••••9fQx · verified 16 Oct 2026, 3:41 pm". */
export function describeSecretStatus(
  status: SecretFieldStatus | null | undefined,
  formatDate: (iso: string) => string = (iso) => iso,
): string | null {
  if (!status?.set) return null;
  const ending = status.last4 ? `, ending ••••${status.last4}` : "";
  const verified = status.verifiedAt
    ? ` · verified ${formatDate(status.verifiedAt)}`
    : "";
  return `Saved${ending}${verified}`;
}

export interface SecretInputProps
  extends Omit<InputProps, "value" | "defaultValue" | "type" | "onChange"> {
  /** What the person has typed in this session: never a stored secret. */
  value: string;
  onChange: (value: string) => void;
  status?: SecretFieldStatus | null;
  /** Formats `verifiedAt` (e.g. to the AD/BS preference). */
  formatDate?: (iso: string) => string;
}

/**
 * A write-only secret input. It shows only what is typed now; a saved secret
 * is described by its last four characters, never put back in the field,
 * and there is no "show" toggle. Autofill is switched off so a browser
 * password cannot slip in either.
 */
export function SecretInput({
  value,
  onChange,
  status,
  formatDate,
  placeholder,
  ...props
}: SecretInputProps) {
  const description = describeSecretStatus(status, formatDate);
  return (
    <Flex direction="column" gap={1} w="100%">
      <Input
        {...props}
        type="password"
        value={value}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        data-secret-input="true"
        placeholder={
          status?.set ? "Type a new value to replace the saved one" : placeholder
        }
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onChange(event.target.value)
        }
      />
      {description && (
        <Text fontSize="xs" color="gray.500" data-testid="secret-status">
          {description}
        </Text>
      )}
    </Flex>
  );
}

interface SecretInputFieldProps {
  name: string;
  id?: string;
  placeholder?: string;
  isDisabled?: boolean;
  secret?: SecretFieldStatus | null;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  formatDate?: (iso: string) => string;
}

/**
 * `type: "secret"` in the `@picovico/base` schema forms. The Formik value is
 * whatever was typed in this session and nothing else: a value the form was
 * initialised with (say, a secret a server wrongly sent back) is wiped on
 * mount, so it is neither shown nor submitted again. Empty means "keep the
 * saved one"; the caller leaves the key out of the request.
 */
export function SecretInputField({
  name,
  id,
  placeholder,
  isDisabled,
  secret,
  onBlur,
  formatDate,
}: SecretInputFieldProps) {
  const { values, setFieldValue } = useFormikContext();
  const [typed, setTyped] = useState("");
  const current = get(values, name, "");

  useEffect(() => {
    if ((current ?? "") !== typed) void setFieldValue(name, typed, false);
  }, [current, typed, name, setFieldValue]);

  return (
    <SecretInput
      id={id ?? name}
      name={name}
      value={typed}
      onChange={setTyped}
      onBlur={onBlur}
      isDisabled={isDisabled}
      placeholder={placeholder}
      status={secret}
      formatDate={formatDate}
    />
  );
}

export default SecretInputField;
